/**
 * 排序函数工厂
 * - createOptimizedSortFunction: 基础分组排序的预编译工厂
 * - createOptimizedAdvancedSortFunction: 高级分组排序的预编译工厂
 *
 * 适合在高频调用场景（如 tooltip 渲染）中复用，避免重复生成优化函数集合。
 */

import {
  IAdvancedSortConfig,
  ISortConfig,
  IInternalSortOptions,
} from './types';
import {
  optimizeFunctions,
  sortByGroupedPrimaryKeyFullCore,
  sortByGroupedPrimaryKeyLightCore,
} from './coreGroupedSort';

/**
 * 创建优化后的排序函数工厂
 * 根据配置生成预编译的优化函数，消除运行时类型判断，提升性能。
 */
export function createOptimizedSortFunction<T>(
  config: ISortConfig<T>,
): (items: T[]) => T[] {
  // 预编译优化函数（工厂创建时优化，而非每次调用时优化）
  const functions = optimizeFunctions(config);

  // 如果没有传入 primaryKey，返回一个直接返回原数组的函数
  if (!functions) {
    return (items: T[]) => items;
  }

  // 返回调用内部核心排序函数的包装函数
  return (items: T[]): T[] => {
    // 入参兜底校验：只接受数组类型
    if (!Array.isArray(items)) {
      console.error(
        'createOptimizedSortFunction expects an array, received:',
        typeof items,
      );
      return [];
    }
    // 单个或为空的场景无需处理排序，直接返回
    if (items.length <= 1) return items;

    // 调用轻量核心排序函数（使用预编译的优化函数）
    return sortByGroupedPrimaryKeyLightCore(items, functions);
  };
}

/**
 * 创建高级排序函数工厂
 * 支持主值选择与组内排序策略的预编译优化。
 */
export function createOptimizedAdvancedSortFunction<T>(
  config: IAdvancedSortConfig<T>,
): (items: T[]) => T[] {
  const functions = optimizeFunctions(config);

  if (!functions) {
    return (items: T[]) => items;
  }

  const internalOptions: IInternalSortOptions<T> = {
    primaryValueSelector: config.primaryValueSelector,
    groupOrderComparator: config.groupOrderComparator,
  };

  return (items: T[]): T[] => {
    if (!Array.isArray(items)) {
      console.error(
        'createOptimizedAdvancedSortFunction expects an array, received:',
        typeof items,
      );
      return [];
    }
    if (items.length <= 1) return items;

    return sortByGroupedPrimaryKeyFullCore(items, functions, internalOptions);
  };
}


